import { Button } from '@edsn/pie-components';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { Fragment, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { Dialog } from '#pie/components/layout/dialog/Dialog';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import { useToast } from '#pie/components/toast/ToastContext';
import type {
  CprContactGroupMembership,
  CprContactResponse,
} from '#pie/internal/api/pie-bff';
import {
  getManagementContacts,
  useDeleteManagementContact,
  useGetCprManagementContacts,
} from '#pie/internal/api/pie-bff';
import { emptyFilterAtom } from '#pie/stores/searchOnlyFilter';

interface RemoveContactPersonButtonProps {
  data?: CprContactResponse;
  usersContactGroups?: CprContactGroupMembership[];
  contactpersonId: string;
}

export const mapContactGroups = (memberships?: CprContactGroupMembership[]) => {
  const groups =
    memberships
      ?.filter((m) => m.contactGroup?.id)
      .map((m) => ({
        id: m.contactGroup!.id,
        required: m.contactGroup!.isRequired ?? false,
        organisationId: m.subunit?.id ?? null,
      })) ?? [];

  return {
    groups,
    hasRequiredGroup: groups.some((g) => g.required),
    requiredGroupIds: groups.filter((g) => g.required).map((g) => g.id),
  };
};

export const RemoveCprContactpersonButton = ({
  data,
  contactpersonId,
  usersContactGroups,
}: RemoveContactPersonButtonProps) => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const { t: tcom } = useTranslation();
  const { t: tom } = useTranslation('organisationManagement');
  const queryClient = useQueryClient();
  const filters = useAtomValue(emptyFilterAtom);

  const { data: contactGroups } = useGetCprManagementContacts(
    {
      pagination: { pageIndex: 0, pageSize: 100 },
      filter: filters,
    },
    {
      query: {
        enabled: openDeleteDialog,
      },
    },
  );

  const linkedRequiredGroups = contactGroups?.items.filter(
    (
      (seenIds) => (cg) =>
        usersContactGroups?.some(
          (ucg) => ucg.contactGroup.id === cg.contactGroup.id,
        ) &&
        cg.contactGroup.isRequired &&
        !seenIds.has(cg.contactGroup.id) &&
        seenIds.add(cg.contactGroup.id)
    )(new Set()),
  );

  const lastRequiredGroups = linkedRequiredGroups?.filter((group) => {
    return !contactGroups?.items.some(
      (item) =>
        item.contactGroup.id === group.contactGroup.id &&
        item.contactId != null &&
        item.contactId !== data?.id,
    );
  });

  const hasRequiredGroups =
    linkedRequiredGroups != null && linkedRequiredGroups.length > 0;

  const lastInRequiredGroup =
    lastRequiredGroups != null && lastRequiredGroups.length > 0;

  const requiredGroupLinks = (
    lastRequiredGroups ??
    linkedRequiredGroups ??
    []
  ).map(({ contactGroup }) => ({
    id: contactGroup.id,
    name: contactGroup.name,
    to: `/beheer/contactgroepenbeheer/${contactGroup.id}`,
  }));

  const { mutate, isPending: isLoading } = useDeleteManagementContact({
    mutation: {
      onError: (error) => {
        const errorDescription = error as unknown as { description: string };
        addToast({
          message: `${tcom('toast_server_error')}${errorDescription?.description}`,
          title: `${tom('cpr_management_detail.error.delete_contactperson')}`,
          type: 'error',
          error: error,
        });
      },
      onSuccess: () => {
        addToast({
          message: `${tcom('common.success.message.delete', { name: data?.fullName })}`,
          title: `${tom('delete.title.deleted_contactperson')}`,
          type: 'success',
        });
        setOpenDeleteDialog(false);
        queryClient.invalidateQueries({
          predicate: (m) => {
            /* istanbul ignore next -- @preserve */
            return m.queryKey.some(
              (n) =>
                typeof n === 'string' && n.includes(getManagementContacts.name),
            );
          },
        });
        queryClient.invalidateQueries({
          queryKey: ['/cpr-management-contact'],
        });
        navigate('/beheer/contactpersonenbeheer');
      },
    },
  });

  return (
    <>
      <Button
        action="danger"
        emphasis="minimal"
        onClick={() => setOpenDeleteDialog(true)}
        className="ml-auto"
        aria-label={tom('delete.title.delete_contactperson')}
      >
        {tom('delete.title.delete_contactperson')}
      </Button>
      <Dialog
        title={
          lastInRequiredGroup
            ? tom('delete_contact.title.delete_not_possible')
            : tom('delete.title.delete_contactperson')
        }
        open={openDeleteDialog}
        onOpenChange={setOpenDeleteDialog}
      >
        <Stack gap="lg">
          {lastInRequiredGroup ? (
            <Text>
              {tom('delete.contact.message.contactgroup_required_1', {
                name: data?.fullName,
                count: lastRequiredGroups?.length,
              })}{' '}
              {requiredGroupLinks.map((group, index) => (
                <Fragment key={group.id}>
                  {index > 0 ? ', ' : null}
                  <Link to={group.to} className="text-content-link">
                    {group.name}
                  </Link>
                </Fragment>
              ))}
              {'. '}
              {tom('delete.contact.message.contactgroup_required_2', {
                name: data?.fullName,
              })}
            </Text>
          ) : hasRequiredGroups ? (
            <Text>
              <Trans
                i18nKey="delete.contact.message.contactgroup"
                t={tom}
                values={{
                  groups: linkedRequiredGroups
                    .map((cg) => cg.contactGroup.name)
                    .join(', '),
                  name: data?.fullName,
                }}
                count={linkedRequiredGroups?.length}
              />
            </Text>
          ) : (
            <Text>
              {tom('delete.message.delete_user', { name: data?.fullName })}
            </Text>
          )}

          <div className="grid-auto grid auto-cols-fr grid-flow-col gap-2">
            <Button
              action="tertiary"
              emphasis="subtle"
              onClick={() => {
                setOpenDeleteDialog(false);
              }}
            >
              {lastInRequiredGroup
                ? tom('button.back')
                : tcom('common.button.cancel')}
            </Button>
            <Button
              action="danger"
              emphasis="bold"
              isLoading={isLoading}
              onClick={() => mutate({ id: contactpersonId })}
              disabled={lastInRequiredGroup || isLoading}
            >
              {tom('button.delete')}
            </Button>
          </div>
        </Stack>
      </Dialog>
    </>
  );
};
